export class MercureClient {
  constructor(hubUrl, toastInstance = null) {
    this.hubUrl = hubUrl;
    this.toast = toastInstance;
    this.eventSource = null;
    this.subscriptions = new Map();
    this.currentTopics = new Set();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.isDev = import.meta.env.DEV;
    this.connectTimeout = null;
  }

  log(message, data = null, type = "info") {
    if (!this.isDev) return;

    const prefix = `%c Mercure `;
    const baseStyle = "padding: 2px 4px; border-radius: 3px; font-weight: bold; color: white;";

    const styles = {
      info: `${baseStyle} background: #3498db;`,
      success: `${baseStyle} background: #2ecc71;`,
      warn: `${baseStyle} background: #f1c40f; color: #333;`,
      error: `${baseStyle} background: #e74c3c;`,
    };

    const args = [prefix, styles[type], message];
    if (data !== null) args.push(data);

    console[type === "success" ? "log" : type === "info" ? "log" : type](...args);
  }

  connect() {
    if (this.connectTimeout) clearTimeout(this.connectTimeout);

    this.connectTimeout = setTimeout(() => {
      const newTopicList = Array.from(this.subscriptions.keys()).sort();

      if (newTopicList.length === 0) {
        if (this.eventSource) {
          this.log("Closing connection (no active subscriptions)");
          this.disconnect();
        }
        return;
      }

      const currentList = Array.from(this.currentTopics).sort();
      const hasChanged = JSON.stringify(newTopicList) !== JSON.stringify(currentList);

      if (this.eventSource?.readyState === EventSource.OPEN && !hasChanged) {
        return;
      }

      if (this.eventSource) {
        this.log(
          "Topic change detected, rotating connection...",
          {
            from: currentList,
            to: newTopicList,
          },
          "warn",
        );
        this.disconnect(false);
      }

      const url = new URL(this.hubUrl);
      newTopicList.forEach((topic) => url.searchParams.append("topic", topic));

      this.log("Connecting to Hub...", newTopicList);
      this.eventSource = new EventSource(url.toString(), { withCredentials: true });

      this.eventSource.onopen = () => {
        this.log("Connected", url.toString(), "success");
        this.reconnectAttempts = 0;
        this.currentTopics = new Set(newTopicList);
      };

      this.eventSource.onerror = (e) => {
        this.log("Connection Lost", e, "error");
        this.handleReconnect();
      };

      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // When the hub uses UUIDs for lastEventId, we need to match based on
          // which subscribed topics this connection is listening to
          // Since all events coming through this EventSource are for our subscribed topics,
          // we can safely deliver to all callbacks

          let matched = false;

          // Try to extract topic from data if available
          const dataTopic = data.topic || data["@id"];

          if (dataTopic) {
            // If data contains topic info, use it for matching
            this.subscriptions.forEach((callbacks, subscribedTopic) => {
              if (subscribedTopic === dataTopic || dataTopic.includes(subscribedTopic)) {
                callbacks.forEach((cb) => cb(data));
                matched = true;
              }
            });
          }

          // If no match yet, deliver to all subscriptions since this EventSource
          // is connected only to topics we explicitly subscribed to
          if (!matched) {
            this.subscriptions.forEach((callbacks) => {
              callbacks.forEach((cb) => cb(data));
            });
            matched = true;
          }
        } catch (err) {
          this.log("Parse Error", err, "error");
        }
      };
    }, 50);
  }

  disconnect(resetTopics = true) {
    if (this.eventSource) {
      this.log("Disconnecting...");
      this.eventSource.close();
      this.eventSource = null;
    }
    if (resetTopics) this.currentTopics.clear();
  }

  subscribe(topic, callback) {
    if (!this.subscriptions.has(topic)) {
      this.subscriptions.set(topic, new Set());
    }
    this.subscriptions.get(topic).add(callback);
    // Removed individual subscription log - batch logging handled by AppMercureSubscriber
    this.connect();
    return () => this.unsubscribe(topic, callback);
  }

  unsubscribe(topic, callback) {
    const callbacks = this.subscriptions.get(topic);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.subscriptions.delete(topic);
        // Removed individual unsubscription log - batch logging handled by AppMercureSubscriber
      }
    }
    this.connect();
  }

  handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.log("Max reconnect attempts reached.", "", "error");
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * 2 ** (this.reconnectAttempts - 1);
    this.log(`Attempting reconnect #${this.reconnectAttempts} in ${delay}ms...`, "", "warn");

    setTimeout(() => this.connect(), delay);
  }
}

// Singleton instance
let mercureClient = null;

export const getMercureClient = (
  hubUrl = import.meta.env.VITE_MERCURE_HUB_URL,
  toastInstance = null,
) => {
  if (!mercureClient) {
    mercureClient = new MercureClient(hubUrl, toastInstance);
  }
  return mercureClient;
};
